// Tracking utility for UTM and tracking parameters
import { setStorage, getStorage } from './storage';

interface TrackingParams {
  [key: string]: string;
}

interface TrackingData {
  params: TrackingParams;
  timestamp: string;
  page: string;
  referrer: string;
}

class TrackingManager {
  private trackingKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'pubid',
    'ref_id',
    'sub6',
    'sub4',
    'sub5',
    'sub18',
    'sub19',
    'cmpid',
    'prelandername',
    'gclid',
    'fbclid',
    'msclkid',
    'clickid',
    'rtkcid',
  ];

  // Get all URL parameters
  getUrlParams(): TrackingParams {
    const params = new URLSearchParams(window.location.search);
    const result: TrackingParams = {};

    params.forEach((value, key) => {
      result[key] = value;
    });

    return result;
  }

  // Test localStorage availability
  testLocalStorage(): boolean {
    try {
      const testKey = '__utm_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Save tracking parameters
  saveTrackingParams(params: TrackingParams): boolean {
    if (!this.testLocalStorage()) {
      return false;
    }

    if (Object.keys(params).length === 0) {
      return false;
    }

    try {
      setStorage('trackingParams', params);
      setStorage('utm_data', params);
      setStorage<TrackingData>('trackingData', {
        params,
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
        referrer: document.referrer,
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  // Get saved tracking parameters
  getSavedTrackingParams(): TrackingParams {
    try {
      const saved = getStorage<TrackingParams>('trackingParams');
      if (saved) {
        return saved;
      }
      return {};
    } catch (error) {
      return {};
    }
  }

  // Update buttons and links with tracking parameters
  updateButtons(params: TrackingParams): void {
    const buttons = document.querySelectorAll<HTMLAnchorElement | HTMLButtonElement>('a, button');

    if (!buttons.length) {
      return;
    }

    // Merge current params with saved params (current params take priority)
    const savedParams = this.getSavedTrackingParams();
    const allParams = { ...savedParams, ...params };

    const paramString = Object.keys(allParams)
      .map((key) => `${key}=${encodeURIComponent(allParams[key])}`)
      .join('&');

    if (paramString) {
      buttons.forEach((button) => {
        if (button.tagName === 'A' && (button as HTMLAnchorElement).href) {
          try {
            const anchor = button as HTMLAnchorElement;
            const url = new URL(anchor.href);
            // Only add params to internal links or if href is relative
            if (
              url.hostname === window.location.hostname ||
              anchor.href.startsWith('/') ||
              anchor.href.startsWith('./')
            ) {
              url.search = paramString;
              anchor.href = url.toString();
            }
          } catch (error) {
            // Skip invalid URLs
          }
        }
      });
    }
  }

  // Clear tracking data
  clearTrackingData(): boolean {
    try {
      // Use our storage utility to clear tracking data
      localStorage.removeItem('trackingParams');
      localStorage.removeItem('utm_data');
      localStorage.removeItem('trackingData');
      sessionStorage.removeItem('trackingParams');
      sessionStorage.removeItem('utm_data');
      return true;
    } catch (error) {
      return false;
    }
  }

  // Initialize tracking
  init(): boolean {
    try {
      // Get URL parameters
      const params = this.getUrlParams();

      // Save tracking parameters
      this.saveTrackingParams(params);

      // Update buttons with parameters
      this.updateButtons(params);

      // Set up observer for dynamic content
      const observer = new MutationObserver(() => {
        this.updateButtons(params);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  // Get tracking parameters for API calls
  getTrackingParamsForAPI(): TrackingParams {
    const saved = this.getSavedTrackingParams();
    const current = this.getUrlParams();
    const result = { ...saved, ...current };
    return result;
  }
}

// Create singleton instance
const trackingManager = new TrackingManager();

// Export functions
export const initTracking = () => trackingManager.init();
export const getTrackingParams = () => trackingManager.getTrackingParamsForAPI();
export const clearTracking = () => trackingManager.clearTrackingData();
export const updateTrackingButtons = (params: TrackingParams) => trackingManager.updateButtons(params);

// Make available globally for debugging
declare global {
  interface Window {
    trackingManager: TrackingManager;
    initTracking: typeof initTracking;
    getTrackingParams: typeof getTrackingParams;
    clearTracking: typeof clearTracking;
  }
}

if (typeof window !== 'undefined') {
  window.trackingManager = trackingManager;
  window.initTracking = initTracking;
  window.getTrackingParams = getTrackingParams;
  window.clearTracking = clearTracking;
}
